import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CircleNotch, Plus } from "@phosphor-icons/react";
import { PieChart, Pie, Cell, Tooltip as ReTooltip } from "recharts";

const NAVY = "#002248";
const GREEN = "#65AB15";
const BLUE = "#2E86D6";
const GRAY = "#9CA3AF";

function StatChip({ label, value, tone, testId, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-baseline gap-1 rounded-sm px-1.5 py-0.5 text-[11px] font-semibold transition-opacity hover:opacity-75 ${tone || "bg-[#F4F4F5] text-[#52525B]"}`}
      data-testid={testId}>
      <span className="font-heading text-sm font-black">{value}</span> {label}
    </button>
  );
}

function StartBatchDialog({ onClose, onStarted }) {
  const [batch, setBatch] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/seven-touch/next-batch")
      .then(({ data }) => {
        setBatch(data);
        setSelected(new Set(data.proposed.map((p) => p.id)));
      })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [onClose]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else if (next.size < (batch?.available_slots || 0)) next.add(id);
    else toast.error(`Only ${batch.available_slots} slot(s) available`);
    return next;
  });

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/seven-touch/start-batch", { prospect_ids: [...selected] });
      toast.success(`${selected.size} campaign(s) started`);
      onStarted();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ p }) => (
    <label className="flex cursor-pointer items-center gap-2.5 border-b border-[#F4F4F5] px-3 py-2 text-sm last:border-0 hover:bg-[#FAFAFA]"
      data-testid={`batch-row-${p.id}`}>
      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
        data-testid={`batch-check-${p.id}`} />
      <span className="min-w-0 flex-1 truncate font-semibold text-[#09090B]">{p.organization_name}</span>
      <span className="shrink-0 text-xs text-[#6D6D70]">Entered {p.entered || "—"}</span>
    </label>
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-lg sm:max-w-lg" data-testid="start-batch-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#002248]">Start Next 10 — review the batch</DialogTitle>
          <DialogDescription>
            Eligible prospects are assigned to you, have valid contact info, and have never run a
            campaign — oldest first. Uncheck any and pick a replacement from the alternates below.
          </DialogDescription>
        </DialogHeader>
        {!batch ? (
          <div className="flex justify-center py-10"><CircleNotch size={22} className="animate-spin text-[#002248]" /></div>
        ) : batch.eligible_total === 0 ? (
          <p className="py-6 text-center text-sm text-[#6D6D70]" data-testid="batch-empty">
            No eligible prospects right now — every assigned prospect either ran a campaign already or is missing contact info.
          </p>
        ) : (
          <>
            <p className="text-xs font-semibold text-[#52525B]" data-testid="batch-slots">
              {selected.size} of {batch.available_slots} available slot(s) selected · {batch.active}/{batch.max_active} campaigns already active
            </p>
            <div className="max-h-52 overflow-y-auto rounded-md border border-[#E4E4E7]" data-testid="batch-proposed">
              {batch.proposed.map((p) => <Row key={p.id} p={p} />)}
            </div>
            {batch.alternates.length > 0 && (
              <>
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#6D6D70]">Alternates (replace an unchecked pick)</p>
                <div className="max-h-40 overflow-y-auto rounded-md border border-[#E4E4E7]" data-testid="batch-alternates">
                  {batch.alternates.map((p) => <Row key={p.id} p={p} />)}
                </div>
              </>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !batch || selected.size === 0} onClick={start}
            className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
            data-testid="start-batch-confirm">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : `Start ${selected.size} campaign(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// 7-Touch card (items 1 & 2): mutually exclusive donut buckets that reconcile
// exactly to the campaign total, separate eligible/active/due/overdue/completed
// counts, and the "Start Next 10" batch workflow (salespeople only).
// Batch G: every chip, legend row and chart section opens the drill-down drawer
// listing the prospects behind that number.
export function SevenTouchCard({ st, isAdmin, onChanged, onDrill }) {
  const navigate = useNavigate();
  const [batchOpen, setBatchOpen] = useState(false);
  const b = st?.buckets || {};
  const drill = (key, label) => onDrill({ type: "seven_touch", key, title: `7-Touch Campaigns — ${label}` });
  const data = [
    { label: "Started — no touches yet", key: "not_touched", count: b.not_touched || 0, color: GRAY },
    { label: "Touches 1–3", key: "touches_1_3", count: b.touches_1_3 || 0, color: NAVY },
    { label: "Touches 4–6", key: "touches_4_6", count: b.touches_4_6 || 0, color: GREEN },
    { label: "Completed (7 touches)", key: "completed_7", count: b.completed_7 || 0, color: BLUE },
  ].filter((d) => d.count > 0).map((d) => ({ ...d, onClick: () => drill(d.key, d.label) }));
  return (
    <div className="rounded-lg border border-[#E4E4E7] bg-white p-4 shadow-sm" data-testid="chart-seven-touch">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-[#002248]">7-Touch Campaigns</h3>
        {!isAdmin && (
          <button type="button" onClick={() => setBatchOpen(true)}
            className="inline-flex items-center gap-1 rounded-sm bg-[#569A1F] px-2 py-1 text-[11px] font-bold text-white hover:bg-[#457d18]"
            data-testid="start-next-10-btn">
            <Plus size={12} weight="bold" /> Start Next 10
          </button>
        )}
      </div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        <StatChip label="eligible" value={st?.eligible ?? 0} testId="seven-touch-eligible"
          onClick={() => drill("eligible", "Eligible")} />
        <StatChip label={`active / ${st?.max_active || 10}`} value={st?.active ?? 0}
          tone="bg-[#002248]/8 text-[#002248]" testId="seven-touch-active"
          onClick={() => drill("active", "Active")} />
        <StatChip label="due today" value={st?.due_today ?? 0}
          tone="bg-[#65AB15]/12 text-[#4c840f]" testId="seven-touch-due"
          onClick={() => drill("due_today", "Due Today")} />
        <StatChip label="overdue" value={st?.overdue ?? 0}
          tone={st?.overdue > 0 ? "bg-[#DC2626]/10 text-[#DC2626]" : undefined} testId="seven-touch-overdue"
          onClick={() => drill("overdue", "Overdue")} />
        <StatChip label="completed" value={st?.completed ?? 0}
          tone="bg-[#2E86D6]/10 text-[#2E86D6]" testId="seven-touch-completed"
          onClick={() => drill("completed", "Completed")} />
      </div>
      {st && st.campaigns > 0 ? (
        <div className="flex items-center gap-2">
          <div className="relative h-36 w-36 shrink-0" data-testid="seven-touch-donut">
            <PieChart width={144} height={144}>
              <Pie data={data} dataKey="count" nameKey="label" innerRadius={42} outerRadius={62}
                paddingAngle={2} strokeWidth={0} className="cursor-pointer"
                onClick={(d) => d?.payload?.onClick && d.payload.onClick()}>
                {data.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <ReTooltip wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-heading text-xl font-black text-[#002248]" data-testid="seven-touch-total">{st.campaigns}</span>
              <span className="text-[10px] text-[#9CA3AF]">Campaigns</span>
            </div>
          </div>
          <div className="min-w-0 flex-1 space-y-1.5">
            {data.map((d) => (
              <button key={d.label} type="button" onClick={d.onClick}
                data-testid={`seven-touch-legend-${d.key}`}
                className="flex w-full cursor-pointer items-start gap-1.5 text-left text-[11px] leading-snug text-[#52525B] hover:underline">
                <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: d.color }} />
                <span className="min-w-0 break-words">{d.label}</span>
                <span className="ml-auto shrink-0 whitespace-nowrap font-semibold text-[#002248]">
                  {d.count} ({Math.round((d.count / st.campaigns) * 100)}%)
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="flex h-32 items-center justify-center text-center text-xs text-[#9CA3AF]">
          {isAdmin
            ? "No 7-touch campaigns running yet."
            : "No campaigns yet — use Start Next 10 to begin working your oldest eligible prospects."}
        </p>
      )}
      {!isAdmin && (
        <button type="button" onClick={() => navigate("/prospects")}
          className="mt-2 text-[11px] font-bold text-[#2E86D6] hover:underline" data-testid="seven-touch-view-prospects">
          View prospects
        </button>
      )}
      {batchOpen && <StartBatchDialog onClose={() => setBatchOpen(false)} onStarted={onChanged} />}
    </div>
  );
}
